import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronRight, Circle, Plus, RefreshCw } from 'lucide-react';
import { createInbox, listBotAudiences, patchInbox, setBotInboxes } from '../../api/endpoints';
import { widgetInboxId } from '../../api/siteIntegrations';
import type { BotProfile, Inbox, ManagedInbox, SiteIntegrations, WebsiteChannel } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { CodeBlock, FlowStep, SectionHead } from '../../components/setup/SetupKit';
import { Spinner } from '../../components/Spinner';
import { useTimeFormat } from '../../lib/tenantTime';
import { useLoad } from '../../lib/useLoad';
import {
  BUTTON_SNIPPET,
  type ChatSection,
  DEFAULT_WIDGET_COLOR,
  IDENTITY_SNIPPET,
  chatChecklist,
  chatLive,
  chatSnippet,
  sameOrigins,
  siteList,
  siteName,
} from './botSetup';

export type WebsiteInbox = ManagedInbox & { channelType: 'website'; channel: WebsiteChannel };

/** Steps on the chat the booking widgets carry: done for them on those pages. */
const ON_WIDGET_PAGES = { label: 'Automatic on widget pages', tone: 'ok' } as const;

/**
 * Getting the chat onto a site, and knowing that it is.
 *
 * The analytics install's shape, for the chat: the steps in the order they are
 * done, beside whether it worked and what is left. It replaces a setup that
 * was spread over four places — a website chat created on the Connect screen,
 * the assistant pointed at it on a tab called Channels & install (whose
 * snippet appeared only after a save), the allowed sites under the widget's
 * look, and nothing at all saying whether any of it had worked.
 *
 * Every tick is read off the channel and the profile rather than asserted.
 * "Live" is the widget's own boot stamping the channel, so it turns on the
 * first time a real page loads it — the Playground, being the console, never
 * counts.
 */
export function InstallSection({
  profile,
  inbox,
  theirs,
  mayCreate,
  integrations,
  refreshing,
  onCheck,
  onProfile,
  onInbox,
  onCreated,
  onGo,
}: {
  profile: BotProfile;
  /** The website chat being set up, or null when the reader has none to manage. */
  inbox: WebsiteInbox | null;
  /** A website chat exists, but it is somebody else's to configure. */
  theirs: boolean;
  /** `connectChannels` — creating a channel is an account-level permission. */
  mayCreate: boolean;
  integrations: SiteIntegrations | null;
  refreshing: boolean;
  onCheck: () => void;
  onProfile: (fresh: BotProfile) => void;
  onInbox: (fresh: Inbox) => void;
  onCreated: (fresh: Inbox) => void;
  onGo: (section: ChatSection) => void;
}) {
  if (!inbox) {
    return (
      <>
        <SectionHead title="Install">Put the chat on your website, then check that it is live.</SectionHead>
        {mayCreate ? (
          <ol className="an-flow">
            <FlowStep n={1} title="Create the website chat" tag={{ label: 'Start here', tone: 'accent' }}>
              <p>
                The chat bubble on your site is a channel of its own, like a mailbox or a phone number. Name it
                and say where it will live; you get the code to paste the moment it exists.
              </p>
              <CreateChatForm profile={profile} onProfile={onProfile} onCreated={onCreated} />
            </FlowStep>
            <FlowStep n={2} title="Paste the snippet on your site" tag={{ label: 'Next' }}>
              <p>A few lines before the closing body tag of every page that should show the chat.</p>
            </FlowStep>
            <FlowStep n={3} title="Check it is live" tag={{ label: 'Next' }}>
              <p>Open any page of your site once, then press Check again here — it turns to Live as soon as a page has loaded the chat.</p>
            </FlowStep>
          </ol>
        ) : (
          <Note tone="info">
            {theirs
              ? 'Your company’s website chat is configured by somebody else. Ask an administrator for Manage on it under Settings → Users & access, and its install steps appear here.'
              : 'There is no website chat yet, and adding a channel needs the Connect channels permission. An administrator grants it under Settings → Users & access.'}
          </Note>
        )}
      </>
    );
  }
  return (
    <ChatSetup
      key={inbox.id}
      profile={profile}
      inbox={inbox}
      integrations={integrations}
      refreshing={refreshing}
      onCheck={onCheck}
      onProfile={onProfile}
      onInbox={onInbox}
      onGo={onGo}
    />
  );
}

function ChatSetup({
  profile,
  inbox,
  integrations,
  refreshing,
  onCheck,
  onProfile,
  onInbox,
  onGo,
}: {
  profile: BotProfile;
  inbox: WebsiteInbox;
  integrations: SiteIntegrations | null;
  refreshing: boolean;
  onCheck: () => void;
  onProfile: (fresh: BotProfile) => void;
  onInbox: (fresh: Inbox) => void;
  onGo: (section: ChatSection) => void;
}) {
  const { fmtDateTime } = useTimeFormat();
  const channel = inbox.channel;
  const live = chatLive(channel);
  const answers = profile.inboxIds.includes(inbox.id);
  const sites = channel.allowedOrigins ?? [];
  // The chat the dispatch dashboard's Website chat switch puts on the booking
  // widget pages: they load it, sign their customers in and tell it who they
  // are by themselves. A failed read is "no", and the steps read as before.
  const widgets = widgetInboxId(integrations) === inbox.id;

  const [answerBusy, setAnswerBusy] = useState(false);
  const [answerError, setAnswerError] = useState<string | null>(null);
  const answerHere = async () => {
    setAnswerBusy(true);
    setAnswerError(null);
    try {
      const result = await setBotInboxes([...profile.inboxIds, inbox.id]);
      onProfile({ ...profile, inboxIds: result.inboxIds });
    } catch (problem) {
      setAnswerError(problem instanceof Error ? problem.message : 'Could not point the assistant at this chat.');
    } finally {
      setAnswerBusy(false);
    }
  };

  const status = {
    live: {
      title: `Live on ${siteName(channel.lastSeenOrigin) || 'your site'}`,
      body: 'The chat is loading on your pages. Nothing to do here.',
    },
    quiet: {
      title: 'Not seen in the last 24 hours',
      body: 'On a quiet site that can simply mean nobody visited. Otherwise check the snippet is still on your pages — a redesign is the usual way it goes missing.',
    },
    waiting: {
      title: 'Waiting for the first page load',
      body: widgets
        ? 'Your reservation widget pages carry this chat. Open one of them, or any page you pasted the snippet into, then press Check again.'
        : 'Once the snippet is on your site, open any page of it in a new tab, then press Check again. The Playground does not count — it is not your site.',
    },
  }[live];

  const items = chatChecklist(profile, inbox);
  const doneCount = items.filter((item) => item.done).length;

  return (
    <>
      <SectionHead title="Install">Put the chat on your website, then check that it is live.</SectionHead>

      {widgets ? (
        <Note tone="info">
          Your reservation widget pages carry this chat — switched on in the dispatch dashboard under{' '}
          <b>My Website → Widgets</b>. The snippet is only for your other pages and sites.
        </Note>
      ) : null}

      <div className="an-setup">
        <ol className="an-flow">
          <FlowStep
            n={1}
            done={live !== 'waiting'}
            title="Paste the snippet on every page"
            tag={live !== 'waiting' ? { label: 'Done', tone: 'ok' } : { label: 'Start here', tone: 'accent' }}
          >
            <p>
              Just before the closing <code>&lt;/body&gt;</code> tag of every page that should show the chat. It
              loads in the background and blocks nothing, and changes you make on this screen reach it on each
              visitor’s next page load — you never paste it again.
            </p>
            <SnippetBlock websiteToken={channel.websiteToken} primary={live === 'waiting'} />
          </FlowStep>

          <FlowStep
            n={2}
            done={answers}
            title="Let the assistant answer it"
            tag={answers ? { label: 'Done', tone: 'ok' } : { label: 'Needed', tone: 'accent' }}
          >
            {answers ? (
              <p>
                Conversations on this chat reach {profile.name} first, and it hands them to your team when it
                should.
                {profile.enabled ? null : (
                  <>
                    {' '}
                    It is <b>switched off</b> for now — try it in the{' '}
                    <button type="button" className="an-link" onClick={() => onGo('playground')}>
                      Playground
                    </button>
                    , then switch it on at the top of this screen.
                  </>
                )}
              </p>
            ) : (
              <>
                <p>
                  Without this, a visitor who writes in waits for a person. Your team still sees every
                  conversation either way.
                </p>
                {answerError ? <ErrorNote error={answerError} /> : null}
                <button type="button" className="btn btn-secondary" disabled={answerBusy} onClick={() => void answerHere()}>
                  {answerBusy ? <Spinner /> : null}
                  Answer on {inbox.name}
                </button>
              </>
            )}
          </FlowStep>

          <FlowStep
            n={3}
            done={sites.length > 0}
            title="Keep it to your own site"
            tag={sites.length ? { label: 'Done', tone: 'ok' } : { label: 'Recommended', tone: 'accent' }}
          >
            <p>
              {sites.length
                ? 'Only pages on these sites can show the chat. A page anywhere else gets nothing at all.'
                : 'Right now any website could paste your snippet and show your chat. Name your own, and every other site is refused.'}
            </p>
            <SitesEditor inbox={inbox} onInbox={onInbox} />
          </FlowStep>

          <FlowStep n={4} title="Open it from your own button" tag={{ label: 'Optional' }}>
            <p>
              Works with the chat bubble as well as instead of it — to show no bubble at all, choose{' '}
              <button type="button" className="an-link" onClick={() => onGo('widget')}>
                Your own button
              </button>{' '}
              as the launcher. The snippet above does not change.
            </p>
            <CodeBlock code={BUTTON_SNIPPET} lang="HTML" label="the button code" />
          </FlowStep>

          <FlowStep
            n={5}
            title="Recognise signed-in customers"
            tag={widgets ? ON_WIDGET_PAGES : { label: 'Optional' }}
          >
            <p>
              {widgets
                ? 'Handled on your reservation widget pages already. On your other pages, pass'
                : 'Pass'}{' '}
              your customer’s sign-in token — a LimoSphere customer sign-in, or your site’s own Firebase sign-in —
              and the assistant can look up their reservations without asking for a code; pass <code>null</code> when
              they sign out, so the next person on that browser starts a fresh chat.
            </p>
            <CodeBlock code={IDENTITY_SNIPPET} lang="JavaScript" label="the sign-in lines" />
          </FlowStep>
        </ol>

        <aside className="an-setup-aside">
          <section className={`an-status is-${live === 'live' ? 'ok' : live}`} aria-live="polite">
            <div className="an-status-title">
              <span className="an-status-dot" aria-hidden="true" />
              {status.title}
            </div>
            <p className="an-status-body">{status.body}</p>
            {channel.lastSeenAt ? (
              <p className="an-status-meta">Last loaded {fmtDateTime(channel.lastSeenAt)}</p>
            ) : null}
            <div className="an-status-actions">
              <button type="button" className="btn btn-secondary" disabled={refreshing} onClick={onCheck}>
                <RefreshCw size={14} className={refreshing ? 'spin-anim' : undefined} />
                {refreshing ? 'Checking…' : 'Check again'}
              </button>
            </div>
          </section>

          <section className="an-checklist">
            <header className="an-checklist-head">
              <h3>Finish setting up</h3>
              <span>
                {doneCount} of {items.length} done
              </span>
            </header>
            <div
              className="an-checklist-bar"
              role="progressbar"
              aria-label="Setup progress"
              aria-valuemin={0}
              aria-valuemax={items.length}
              aria-valuenow={doneCount}
            >
              <i style={{ width: `${(doneCount / items.length) * 100}%` }} />
            </div>
            <ul>
              {items.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    className={item.done ? 'an-check is-done' : 'an-check'}
                    onClick={() => onGo(item.section)}
                  >
                    {item.done ? (
                      <CheckCircle2 size={16} className="an-check-icon" />
                    ) : (
                      <Circle size={16} className="an-check-icon" />
                    )}
                    <span className="an-check-text">
                      <span className="an-check-label">{item.label}</span>
                      <span className="an-check-detail">{item.detail}</span>
                    </span>
                    <ChevronRight size={14} className="an-check-go" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </>
  );
}

/**
 * The snippet, with the one choice that changes it: who the page is for.
 *
 * The audiences are SERVED, never listed here — a console offering one the
 * service does not know would hand out a page the assistant answers as a
 * customer anyway. With none but the default there is no choice to draw.
 */
function SnippetBlock({ websiteToken, primary }: { websiteToken: string; primary: boolean }) {
  const audiences = useLoad(listBotAudiences);
  const others = (audiences.data ?? []).filter((row) => !row.isDefault);
  const [audience, setAudience] = useState<string | null>(null);
  const chosen = others.find((row) => row.id === audience) ?? null;
  const code = chatSnippet({ websiteToken, consoleOrigin: window.location.origin, audience: chosen?.id ?? null });

  return (
    <>
      {others.length ? (
        <div className="embed-audience">
          <span className="field-label">Who is this page for?</span>
          <div className="embed-audience-switch">
            <button type="button" className={chosen ? '' : 'is-on'} onClick={() => setAudience(null)}>
              Customers
            </button>
            {others.map((row) => (
              <button
                key={row.id}
                type="button"
                className={chosen?.id === row.id ? 'is-on' : ''}
                onClick={() => setAudience(row.id)}
              >
                {row.label}
              </button>
            ))}
          </div>
          <span className="field-hint">
            {chosen
              ? `${chosen.describe} Paste this version only on pages for ${chosen.label.toLowerCase()}.`
              : 'Your public site. Pages for anyone else get their own version of the snippet.'}
          </span>
        </div>
      ) : null}
      <CodeBlock code={code} lang="HTML" label="the snippet" primary={primary} />
    </>
  );
}

/**
 * The allowed sites, edited where they are explained.
 *
 * What is typed is reduced to the origin the service stores before it is sent
 * (`siteList`), so `example.com/` saves rather than failing on its slash — and
 * the half of a site the exact match would refuse (`www.` or not) is offered
 * as one press, because a site that refuses its own pages looks exactly like a
 * snippet nobody pasted.
 */
function SitesEditor({ inbox, onInbox }: { inbox: WebsiteInbox; onInbox: (fresh: Inbox) => void }) {
  const saved = inbox.channel.allowedOrigins ?? [];
  const [text, setText] = useState(() => saved.join('\n'));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const list = useMemo(() => siteList(text), [text]);
  const dirty = !sameOrigins(list.origins, saved);
  const seen = inbox.channel.lastSeenOrigin;
  const offerSeen = seen && !list.origins.includes(seen) ? seen : null;

  useEffect(() => {
    if (!done) return;
    const timer = setTimeout(() => setDone(false), 2400);
    return () => clearTimeout(timer);
  }, [done]);

  const add = (origin: string) => {
    setText((was) => (was.trim() ? `${was.trimEnd()}\n${origin}` : origin));
    setDone(false);
  };

  const save = async () => {
    if (list.problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const fresh = await patchInbox(inbox.id, { channel: { allowedOrigins: list.origins } });
      onInbox(fresh);
      setText(list.origins.join('\n'));
      setDone(true);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not save the sites.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bot-sites">
      <textarea
        className="textarea an-mono"
        rows={3}
        aria-label="Allowed sites, one per line"
        placeholder={'https://www.example.com\nhttps://example.com'}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setDone(false);
        }}
      />
      {list.problems.length ? (
        <ul className="an-set-problems">
          {list.problems.map((problem, index) => (
            <li key={`${index}-${problem.line}`}>
              <code>{problem.line}</code> {problem.message}
            </li>
          ))}
        </ul>
      ) : null}
      {offerSeen || list.missingTwins.length ? (
        <div className="bot-sites-offers">
          {offerSeen ? (
            <button type="button" className="btn btn-ghost" onClick={() => add(offerSeen)}>
              <Plus size={13} /> {offerSeen} <span className="an-set-muted">— where it last loaded</span>
            </button>
          ) : null}
          {list.missingTwins.map((twin) => (
            <button key={twin} type="button" className="btn btn-ghost" onClick={() => add(twin)}>
              <Plus size={13} /> {twin} <span className="an-set-muted">— refused unless listed too</span>
            </button>
          ))}
        </div>
      ) : null}
      {error ? <ErrorNote error={error} /> : null}
      <div className="an-set-actions">
        <button
          type="button"
          className="btn btn-secondary"
          disabled={busy || !dirty || list.problems.length > 0}
          onClick={() => void save()}
        >
          {busy ? <Spinner /> : null}
          {list.origins.length || !saved.length ? 'Save sites' : 'Allow any site'}
        </button>
        {done ? (
          <span className="an-set-ok">
            <CheckCircle2 size={14} /> Saved — pages elsewhere are now refused.
          </span>
        ) : (
          <span className="an-set-muted">One per line. A path after the address is ignored.</span>
        )}
      </div>
    </div>
  );
}

/**
 * A website chat, created where it is needed: its name and, optionally, the
 * site it is for — which becomes its first allowed site. The assistant is
 * pointed at it in the same breath, because nobody creating a chat from the
 * Chatbot screen means "and have nobody answer it".
 */
export function CreateChatForm({
  profile,
  onProfile,
  onCreated,
  onCancel,
}: {
  profile: BotProfile;
  onProfile: (fresh: BotProfile) => void;
  onCreated: (fresh: Inbox) => void;
  onCancel?: () => void;
}) {
  const [name, setName] = useState('Website chat');
  const [site, setSite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = siteList(site);

  const create = async () => {
    if (!name.trim() || list.problems.length) return;
    setBusy(true);
    setError(null);
    try {
      const inbox = await createInbox({
        name: name.trim(),
        channel: {
          type: 'website',
          widgetColor: DEFAULT_WIDGET_COLOR,
          widgetPosition: 'right',
          allowedOrigins: list.origins,
        },
      });
      // Two writes, as everywhere else on this screen: the channel has to exist
      // before the assistant can own it. A failure on the second leaves an
      // ordinary chat that step 2 offers to finish.
      try {
        const result = await setBotInboxes([...profile.inboxIds, inbox.id]);
        onProfile({ ...profile, inboxIds: result.inboxIds });
      } catch {
        // Step 2 says so, with its own button.
      }
      onCreated(inbox);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not create the website chat.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="bot-create-chat"
      onSubmit={(event) => {
        event.preventDefault();
        void create();
      }}
    >
      <Field label="Name" hint="Your team sees it on conversations from the site.">
        <input className="input" value={name} onChange={(event) => setName(event.target.value)} />
      </Field>
      <Field
        label="Your website (optional)"
        hint="Only pages on it may show the chat. Add more sites, or leave it open to any, in step 3."
        error={list.problems[0] ? `"${list.problems[0].line}" ${list.problems[0].message}` : null}
      >
        <input
          className="input"
          placeholder="https://www.example.com"
          value={site}
          onChange={(event) => setSite(event.target.value)}
        />
      </Field>
      {error ? <ErrorNote error={error} /> : null}
      <div className="an-set-actions">
        {onCancel ? (
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={busy || !name.trim() || list.problems.length > 0}>
          {busy ? <Spinner /> : null}
          Create and get the code
        </button>
      </div>
    </form>
  );
}
